/**
 * Coup de pouce — Attendu 1054 « addition et soustraction de fractions
 * simples : 3/5 + 4/5 ; 1 − 2/3 ; 4/7 − 2/21 ; 3 + 2/5 ; 2/5 + 1/4 ».
 * QUATRE pages STATIQUES, une par cas, partagées par les trois
 * generator_keys ; l'élève choisit sa page par les puces nommées du sheet.
 * Phrases validées en texte nu par le prof le 2026-09-09 (procédure « les
 * phrases avant le dessin »), codées mot pour mot.
 *
 * Pourquoi quatre pages (prof, 2026-09-09) : l'automatisme, c'est d'abord
 * RECONNAÎTRE le cas (« les dénominateurs sont-ils égaux ? sinon l'un est-il
 * un multiple de l'autre ? ») puis d'appliquer le réflexe du cas ; les cartes
 * mélangent les quatre cas, une seule page n'aidait qu'un élève sur quatre.
 * Les puces sont les noms des cas : « Même dénominateur », « Un entier »,
 * « L'un est un multiple », « Aucun lien ».
 *
 * L'image que l'élève a déjà : la droite graduée et ses arcs (1050), la
 * droite de 0 à 4 de la fiche 1053, les deux droites alignées et les flèches
 * × k de la fiche 1051. Deux couleurs par page, dans l'ordre de lecture : le
 * dessin, le calcul en gros (sans la flèche « c'est là », retirée partout le
 * 2026-09-09), puis UNE ligne, le geste,
 * en corps 15 avec ses fractions empilées. Épreuve de Musk du prof
 * (2026-09-09) : la ligne de reconnaissance (« des cinquièmes et des
 * cinquièmes… ») est portée par la puce, la vérification (« je vérifie… »)
 * trouble l'élève qui n'a pas encore calculé ; les deux sont retirées.
 *
 * SACRIFIÉS : les quatre exemples (3/5 + 4/5, 3 + 2/5, 2/3 + 1/6, 2/5 + 1/4)
 * ne sortent d'aucune carte sous aucune forme (gen_1054, auto-testé par
 * cdp1054.self.test.js). Largeur bornée à 340 px, tailles de 1048.
 */

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';

const X0 = 60;
const UNITE = 280;

/** Une fraction empilée, une couleur. */
export const frac = (x, y, n, d, couleur, taille = 14, demi = 10) => `<text x="${x}" y="${y - 4}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${n}</text>
  <line x1="${x - demi}" y1="${y}" x2="${x + demi}" y2="${y}" stroke="${couleur}" stroke-width="1.8"/>
  <text x="${x}" y="${y + taille + 1}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${d}</text>`;

/** Une droite de 0 à `fin` sur `longueur` px, chaque unité partagée en `n` parts, entiers étiquetés dessous. */
const droite = (y, n, fin, longueur = UNITE) => {
    const toX = (t) => X0 + (t * longueur) / fin;
    const grads = Array.from({ length: n * fin + 1 }, (_, k) => {
        const x = toX(k / n);
        const entier = k % n === 0;
        return `<line x1="${x}" y1="${y - (entier ? 9 : 5)}" x2="${x}" y2="${y + (entier ? 9 : 5)}" stroke="${ENCRE}" stroke-width="${entier ? 2 : 1.2}"/>`
            + (entier ? `<text x="${x}" y="${y + 24}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">${k / n}</text>` : '');
    }).join('\n  ');
    return `<line x1="${X0 - 14}" y1="${y}" x2="${toX(fin) + 24}" y2="${y}" stroke="${ENCRE}" stroke-width="2"/>
  <polygon points="${toX(fin) + 32},${y} ${toX(fin) + 22},${y - 5} ${toX(fin) + 22},${y + 5}" fill="${ENCRE}"/>
  ${grads}`;
};

/** `nb` arcs d'une part au-dessus de la droite, de la part `depuis` (en parts), couleur donnée. */
const arcs = (y, toXPart, depuis, nb, couleur) => Array.from({ length: nb }, (_, k) => {
    const x1 = toXPart(depuis + k);
    const x2 = toXPart(depuis + k + 1);
    return `<path d="M ${x1} ${y - 10} Q ${(x1 + x2) / 2} ${y - 30} ${x2} ${y - 10}" fill="none" stroke="${couleur}" stroke-width="1.8"/>`;
}).join('\n  ');

export const svg = (aria, corps, hauteur = 230) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 ${hauteur}" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="${aria}">
  ${corps}
</svg>`.trim();


/**
 * La ligne du geste, sous le dessin : segments de texte (corps 15) et
 * fractions EMPILÉES, centrés sur 400 unités (largeurs estimées : 7,2 unités
 * par caractère, 28 par fraction).
 */
export const ligneGeste = (y, segments) => {
    // Ligne sans fraction : un seul <text> centré et des <tspan> colorés, le navigateur espace lui-même
    // (revue prof 2026-09-18 : la largeur estimée à 7,2 px par lettre laissait des trous, et l'espace en
    // tête d'un <text> séparé était avalé : « 6et dans la table »).
    if (segments.every((s) => !s.f)) {
        return `<text x="200" y="${y + 5}" font-size="15" fill="${ENCRE}" text-anchor="middle">${segments.map((s) => (s.c ? `<tspan fill="${s.c}" font-weight="bold">${s.t}</tspan>` : s.t)).join('')}</text>`;
    }
    const largeur = (s) => (s.f ? 28 : s.t.length * 7.2);
    let x = 200 - segments.reduce((acc, s) => acc + largeur(s), 0) / 2;
    return segments.map((s) => {
        const debut = x;
        x += largeur(s);
        if (s.f) return frac(debut + 14, y, s.f[0], s.f[1], s.c || ENCRE, 13, 10);
        return `<text x="${debut}" y="${y + 5}" font-size="15" fill="${ENCRE}">${s.t}</text>`;
    }).join('\n  ');
};
export const op = (x, y, s) => `<text x="${x}" y="${y}" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}">${s}</text>`;

// ─── Page 1 : Même dénominateur, 3/5 + 4/5 = 7/5 ──────────────────────────

const P1_Y = 92;
const p1X = (parts) => X0 + (parts * UNITE) / 10;
export const SVG_MEME_DEN = svg(
    'Une droite de 0 à 2 partagée en cinquièmes. Trois arcs bleus depuis 0, puis quatre arcs orange, jusqu\'au point 7 sur 5. 3 sur 5 plus 4 sur 5 égale 7 sur 5. 3 parts et 4 parts, ça fait 7 parts : 7 sur 5.',
    `<!-- 1. la droite en cinquièmes, 3 arcs bleus puis 4 arcs orange -->
  ${droite(P1_Y, 5, 2)}
  ${arcs(P1_Y, p1X, 0, 3, BLEU)}
  ${arcs(P1_Y, p1X, 3, 4, ORANGE)}
  <circle cx="${p1X(7)}" cy="${P1_Y}" r="5" fill="${ORANGE}"/>
  ${frac(p1X(1.5), P1_Y - 44, 3, 5, BLEU, 11, 8)}
  ${frac(p1X(5), P1_Y - 44, 4, 5, ORANGE, 11, 8)}
  ${frac(p1X(7), P1_Y + 52, 7, 5, ENCRE, 12, 8)}

  <!-- 2. le résultat -->
  ${frac(120, 204, 3, 5, BLEU, 15, 11)}
  ${op(146, 209, '+')}
  ${frac(172, 204, 4, 5, ORANGE, 15, 11)}
  ${op(198, 209, '=')}
  ${frac(224, 204, 7, 5, ENCRE, 15, 11)}

  <!-- 3. le geste -->
  ${ligneGeste(252, [{ t: '3 parts et 4 parts, ça fait 7 parts : ' }, { f: [7, 5] }, { t: '.' }])}`,
    276,
);

// ─── Page 2 : Un entier, 3 + 2/5 = 15/5 + 2/5 = 17/5 ──────────────────────

const P2_Y = 92;
const p2X = (parts) => X0 + (parts * UNITE) / 20;
const SVG_ENTIER = svg(
    'Une droite de 0 à 4 partagée en cinquièmes. Un trait bleu de 0 à 3, étiqueté 3 égale 15 sur 5. Deux arcs orange de 3 jusqu\'au point 17 sur 5. 3 plus 2 sur 5 égale 15 sur 5 plus 2 sur 5 égale 17 sur 5. 3, c\'est 15 sur 5. 15 parts et 2 parts : 17 sur 5.',
    `<!-- 1. la droite de 0 à 4 en cinquièmes, le trait bleu 0 → 3, deux arcs orange -->
  ${droite(P2_Y, 5, 4)}
  <line x1="${p2X(0)}" y1="${P2_Y + 38}" x2="${p2X(15)}" y2="${P2_Y + 38}" stroke="${BLEU}" stroke-width="2"/>
  <line x1="${p2X(0)}" y1="${P2_Y + 34}" x2="${p2X(0)}" y2="${P2_Y + 42}" stroke="${BLEU}" stroke-width="2"/>
  <line x1="${p2X(15)}" y1="${P2_Y + 34}" x2="${p2X(15)}" y2="${P2_Y + 42}" stroke="${BLEU}" stroke-width="2"/>
  <text x="${p2X(6)}" y="${P2_Y + 60}" font-size="12" font-weight="bold" text-anchor="middle" fill="${BLEU}">3 =</text>
  ${frac(p2X(8.2), P2_Y + 56, 15, 5, BLEU, 12, 9)}
  ${arcs(P2_Y, p2X, 15, 2, ORANGE)}
  <circle cx="${p2X(17)}" cy="${P2_Y}" r="5" fill="${ORANGE}"/>
  ${frac(p2X(16), P2_Y - 44, 2, 5, ORANGE, 11, 8)}

  <!-- 2. le résultat -->
  <text x="70" y="209" font-size="16" font-weight="bold" text-anchor="middle" fill="${BLEU}">3</text>
  ${op(92, 209, '+')}
  ${frac(116, 204, 2, 5, ORANGE, 15, 11)}
  ${op(142, 209, '=')}
  ${frac(170, 204, 15, 5, BLEU, 15, 12)}
  ${op(198, 209, '+')}
  ${frac(222, 204, 2, 5, ORANGE, 15, 11)}
  ${op(248, 209, '=')}
  ${frac(276, 204, 17, 5, ENCRE, 15, 12)}

  <!-- 3. le geste -->
  ${ligneGeste(252, [{ t: '3, c\'est ' }, { f: [15, 5], c: BLEU }, { t: '. 15 parts et 2 parts : ' }, { f: [17, 5] }, { t: '.' }])}`,
    276,
);

// ─── Page 3 : L'un est un multiple, 2/3 + 1/6 = 4/6 + 1/6 = 5/6 ───────────

const Y1 = 56;
const Y2 = 150;
const toX = (t) => X0 + t * UNITE;
const XP = toX(2 / 3);
const XA = toX(5 / 6);
const XM = (XP + XA) / 2;
export const SVG_MULTIPLE = svg(
    'Une droite de 0 à 1 partagée en tiers, le point 2 sur 3 en bleu. Dessous, la même droite, chaque tiers partagé en 2 : des sixièmes ; le même point, 2 sur 3 égale 4 sur 6. Un arc orange d\'une part, de 4 sur 6 à 5 sur 6, plus 1 sur 6. 2 sur 3 plus 1 sur 6 égale 4 sur 6 plus 1 sur 6 égale 5 sur 6. Je partage chaque tiers en 2 : 2 sur 3, c\'est 4 sur 6.',
    `<!-- 1. la droite en tiers, le point 2/3 -->
  ${droite(Y1, 3, 1)}
  <circle cx="${XP}" cy="${Y1}" r="5" fill="${BLEU}"/>
  ${frac(XP, Y1 - 26, 2, 3, BLEU, 12, 8)}

  <!-- 2. la même droite en sixièmes, le même point : 2/3 = 4/6 -->
  ${droite(Y2, 6, 1)}
  <circle cx="${XP}" cy="${Y2}" r="5" fill="${BLEU}"/>
  ${frac(XP - 22, Y2 + 44, 2, 3, BLEU, 12, 8)}
  <text x="${XP}" y="${Y2 + 48}" font-size="12" font-weight="bold" text-anchor="middle" fill="${BLEU}">=</text>
  ${frac(XP + 22, Y2 + 44, 4, 6, BLEU, 12, 8)}

  <!-- 3. l'arc orange d'une part, + 1/6 -->
  <path d="M ${XP} ${Y2 - 10} Q ${XM} ${Y2 - 32} ${XA} ${Y2 - 10}" fill="none" stroke="${ORANGE}" stroke-width="1.8"/>
  <polygon points="${XA},${Y2 - 8} ${XA - 6},${Y2 - 16} ${XA + 1},${Y2 - 17}" fill="${ORANGE}"/>
  <text x="${XM - 14}" y="${Y2 - 40}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ORANGE}">+</text>
  ${frac(XM + 4, Y2 - 44, 1, 6, ORANGE, 11, 8)}

  <!-- 4. le résultat -->
  ${frac(80, 232, 2, 3, BLEU, 15, 11)}
  ${op(106, 237, '+')}
  ${frac(132, 232, 1, 6, ORANGE, 15, 11)}
  ${op(158, 237, '=')}
  ${frac(184, 232, 4, 6, BLEU, 15, 11)}
  ${op(210, 237, '+')}
  ${frac(236, 232, 1, 6, ORANGE, 15, 11)}
  ${op(262, 237, '=')}
  ${frac(288, 232, 5, 6, ENCRE, 15, 11)}

  <!-- 5. le geste -->
  ${ligneGeste(280, [{ t: 'Je partage chaque tiers en 2 : ' }, { f: [2, 3], c: BLEU }, { t: ', c\'est ' }, { f: [4, 6], c: BLEU }, { t: '.' }])}`,
    304,
);

// ─── Page 4 : Aucun lien, 2/5 + 1/4 = 8/20 + 5/20 = 13/20 ─────────────────

/** « a/b = c/d » avec les flèches × k en haut et en bas (l'image de la fiche 1051). */
export const conversion = (x, y, a, b, c, d, k, couleur) => `${frac(x, y, a, b, couleur, 15, 11)}
  <text x="${x + 32}" y="${y + 5}" font-size="15" font-weight="bold" text-anchor="middle" fill="${ENCRE}">=</text>
  ${frac(x + 64, y, c, d, couleur, 15, 12)}
  <path d="M ${x + 8} ${y - 24} Q ${x + 32} ${y - 44} ${x + 56} ${y - 24}" fill="none" stroke="${couleur}" stroke-width="1.6"/>
  <polygon points="${x + 58},${y - 22} ${x + 50},${y - 24} ${x + 55},${y - 30}" fill="${couleur}"/>
  <text x="${x + 32}" y="${y - 38}" font-size="11" font-weight="bold" text-anchor="middle" fill="${couleur}">× ${k}</text>
  <path d="M ${x + 8} ${y + 24} Q ${x + 32} ${y + 44} ${x + 56} ${y + 24}" fill="none" stroke="${couleur}" stroke-width="1.6"/>
  <polygon points="${x + 58},${y + 22} ${x + 50},${y + 24} ${x + 55},${y + 30}" fill="${couleur}"/>
  <text x="${x + 32}" y="${y + 48}" font-size="11" font-weight="bold" text-anchor="middle" fill="${couleur}">× ${k}</text>`;

export const SVG_AUCUN_LIEN = svg(
    '2 sur 5 égale 8 sur 20, avec les flèches fois 4 en haut et en bas. 1 sur 4 égale 5 sur 20, avec les flèches fois 5. 2 sur 5 plus 1 sur 4 égale 8 sur 20 plus 5 sur 20 égale 13 sur 20. 5 fois 4 égale 20 : 2 sur 5 égale 8 sur 20 et 1 sur 4 égale 5 sur 20.',
    `<!-- 1. les deux conversions, flèches × 4 et × 5 -->
  ${conversion(80, 96, 2, 5, 8, 20, 4, BLEU)}
  ${conversion(240, 96, 1, 4, 5, 20, 5, ORANGE)}

  <!-- 2. le résultat -->
  ${frac(66, 204, 2, 5, BLEU, 15, 11)}
  ${op(92, 209, '+')}
  ${frac(118, 204, 1, 4, ORANGE, 15, 11)}
  ${op(144, 209, '=')}
  ${frac(172, 204, 8, 20, BLEU, 15, 12)}
  ${op(200, 209, '+')}
  ${frac(228, 204, 5, 20, ORANGE, 15, 12)}
  ${op(256, 209, '=')}
  ${frac(284, 204, 13, 20, ENCRE, 15, 12)}

  <!-- 3. le geste -->
  ${ligneGeste(252, [{ t: '5 × 4 = 20 : ' }, { f: [2, 5], c: BLEU }, { t: ' = ' }, { f: [8, 20], c: BLEU }, { t: ' et ' }, { f: [1, 4], c: ORANGE }, { t: ' = ' }, { f: [5, 20], c: ORANGE }, { t: '.' }])}`,
    276,
);

// La base commune aux quatre cas, la même phrase sur chaque page, dans
// l'emplacement `court` du sheet (en gras au-dessus du dessin) : elle était le
// titre de l'ancienne page unique et avait disparu au passage à une page par
// cas (prof, 2026-09-09 : « on ne précise pas la base ? »).
export const BASE = 'D\'abord le même dénominateur : des parts de la même taille.';

const AIDE = {
    pages: [
        { titre: 'Même dénominateur', court: BASE, svg: SVG_MEME_DEN },
        { titre: 'Un entier', court: BASE, svg: SVG_ENTIER },
        { titre: 'L\'un est un multiple', court: BASE, svg: SVG_MULTIPLE },
        { titre: 'Aucun lien', court: BASE, svg: SVG_AUCUN_LIEN },
    ],
};

const CP_1054 = {
    flash_texte_trou_saisie: AIDE,
    meme_qcm_unique: AIDE,
    meme_tableau_vf: AIDE,
};

export default CP_1054;
